import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { useFamily } from '../lib/family.jsx';
import {
  approveSpendProposal, createSpendProposal, declineSpendProposal, logSpend, undoSpend, useFuel,
} from '../lib/fuel.js';
import FuelGauge from '../ui/FuelGauge.jsx';
import QuickLog from '../ui/QuickLog.jsx';
import AskFlow from '../ui/AskFlow.jsx';
import ProposalQueue from '../ui/ProposalQueue.jsx';
import History from '../ui/History.jsx';
import { SpotArt } from '../ui/Art.jsx';

export default function FamilyHome() {
  const { family, kids, profile } = useFamily();
  const { user } = useAuth();
  const fuel = useFuel(family?.id);
  const navigate = useNavigate();
  // Only for the write paths this screen adds (ask/approve/decline) — `fuel`
  // already surfaces its own read errors below.
  const [actionError, setActionError] = useState(null);

  return (
    <main className="app-shell stack">
      <div className="spread">
        <div className="stack tight">
          <p className="eyebrow">{family.name}</p>
          <h1>{profile ? profile.display_name : 'Grown-up view'}</h1>
        </div>
        {/* The child's way out is the Switch tab; a parent handing the tablet
            over wants it from here, one tap, without a trip through Family. */}
        {!profile && <Link to="/switch"><button className="quiet">Switch</button></Link>}
      </div>

      {/*
        The gauge sits first, above the crew and everything else. It is the one
        number the whole household is supposed to be able to state without
        being asked to go and check — which only happens if reading it costs
        nothing. Anything above it is something between a child and the number.
      */}
      {!fuel.loading && (
        <FuelGauge
          balanceCents={fuel.projection?.balanceCents ?? 0}
          refillCents={fuel.rule?.amountCents ?? 0}
          cadence={fuel.rule?.cadence ?? 'weekly'}
          today={fuel.today}
          goal={fuel.goal}
          rolledOverCents={fuel.projection?.rolledOverCents ?? 0}
          onSetUp={profile ? null : () => navigate('/fuel/setup')}
        />
      )}

      {/*
        Logging is the parent's job, so the tiles are absent from a child's
        view. The gauge above them is not — seeing the number is the whole
        point, and it is the half of this feature that belongs to everyone.
      */}
      {!profile && fuel.envelope && (
        <QuickLog
          tiles={fuel.tiles}
          onManage={() => navigate('/fuel/tiles')}
          // `tile` is null for a one-off with no tile behind it, and `note` is
          // whatever the parent typed for it — the only thing that will tell
          // it apart in the history later.
          onLog={async (tile, amountCents, note) => {
            const entry = await logSpend({
              familyId: family.id,
              envelopeId: fuel.envelope.id,
              amountCents,
              tileId: tile?.id ?? null,
              note,
              actorId: user.id,
              occurredAt: fuel.today,
            });
            await fuel.reload();
            return entry;
          }}
          onUndo={async (entry) => {
            await undoSpend({ entry, actorId: user.id, occurredAt: fuel.today });
            await fuel.reload();
          }}
        />
      )}

      {/*
        The child's side: pick something, see the impact, ask. Filing moves no
        money — see CLAUDE.md's "Asking moves no money" — so there's nothing
        here to await beyond the write itself.
      */}
      {profile && fuel.envelope && (
        <AskFlow
          tiles={fuel.tiles}
          balanceCents={fuel.projection?.balanceCents ?? 0}
          goal={fuel.goal}
          refillCents={fuel.rule?.amountCents ?? 0}
          cadence={fuel.rule?.cadence ?? 'weekly'}
          onAsk={async ({ amountCents, tileId, note }) => {
            setActionError(null);
            try {
              await createSpendProposal({
                familyId: family.id,
                envelopeId: fuel.envelope.id,
                childId: profile.id,
                amountCents,
                tileId,
                note,
                requiresSecond: fuel.rule?.requiresSecond ?? false,
              });
              await fuel.reload();
            } catch (e) {
              setActionError(`Couldn’t send that ask — ${e.message}.`);
            }
          }}
        />
      )}

      {/*
        The parent's side: yes or no, one tap, with the impact already worked
        out. Response latency is the health metric this feature lives or dies
        on (CLAUDE.md's "The parent's side must take fifteen seconds"), so
        this sits right on the home screen rather than behind a link.
      */}
      {!profile && fuel.envelope && (
        <ProposalQueue
          proposals={(fuel.proposals ?? []).map((p) => ({
            ...p,
            askedBy: kids.find((k) => k.id === p.childId)?.display_name ?? null,
          }))}
          // The full set, archived included: a proposal filed last week against
          // a tile retired since still has to say what it was for.
          tiles={fuel.allTiles}
          balanceCents={fuel.projection?.balanceCents ?? 0}
          goal={fuel.goal}
          refillCents={fuel.rule?.amountCents ?? 0}
          cadence={fuel.rule?.cadence ?? 'weekly'}
          onApprove={async (p) => {
            setActionError(null);
            try {
              await approveSpendProposal({ proposal: p, actorId: user.id, occurredAt: fuel.today });
              await fuel.reload();
            } catch (e) {
              setActionError(`Couldn’t approve that — ${e.message}.`);
            }
          }}
          onDecline={async (p) => {
            setActionError(null);
            try {
              await declineSpendProposal({ proposal: p, actorId: user.id });
              await fuel.reload();
            } catch (e) {
              setActionError(`Couldn’t decline that — ${e.message}.`);
            }
          }}
        />
      )}

      {fuel.error && <p className="notice error">{fuel.error}</p>}
      {actionError && <p className="notice error">{actionError}</p>}

      {fuel.envelope && (
        <History entries={fuel.entries ?? []} tiles={fuel.allTiles} goalName={fuel.goal?.name} />
      )}

      {/*
        The one card that survives on the home screen from the days before the
        tab bar: with nobody on the crew there is nothing for the bank or the
        wishes to be about, and a new parent should not have to find the
        Family tab to learn that.
      */}
      {!profile && kids.length === 0 && (
        <div className="card stack">
          <SpotArt name="empty" />
          <h2>No crew yet</h2>
          <p className="muted">Add the kids and the rest of the app has someone to be about.</p>
          <Link to="/children"><button className="primary">Add a child</button></Link>
        </div>
      )}
    </main>
  );
}
